import React from "react";
import { Card, CardBody, CardHeader } from "reactstrap";
import { useSelector } from "react-redux";
import { createSelector } from "reselect";
import ReactApexChart from "react-apexcharts";
import WidgetLoader from "./WidgetLoader";

const SolicitudesProximoMes = () => {
  const selectLayoutState = (state) => state;

  const pageData = createSelector(selectLayoutState, (state) => ({
    kpis: state.Dashboard.kpis,
    loading: state.Dashboard.loading,
  }));

  const { kpis, loading } = useSelector(pageData);

  // Configuración de colores e iconos para estatus
  const estatusConfig = {
    "Por Renovar": {
      color: "#ff8c00",
      bgColor: "rgba(255, 140, 0, 0.1)",
      icon: "ri-refresh-line",
      badge: "info"
    },
  };

  // Usar la nueva estructura de solicitudes_activas.proximo_mes
  const total = kpis?.solicitudes_activas?.proximo_mes?.total || 0;
  
  // Preparar datos para la gráfica de dona
  const allStatuses = ["Por Renovar"];
  
  // Datos completos para la lista (incluye valores 0)
  const completeData = allStatuses.map(estatus => ({
    estatus,
    cantidad: total,
    color: estatusConfig[estatus]?.color || "#878a99",
    icon: estatusConfig[estatus]?.icon || "ri-file-line"
  }));

  // Datos filtrados para la gráfica (solo valores > 0)
  const chartData = completeData.filter(item => item.cantidad > 0);

  const chartOptions = {
    chart: {
      type: 'donut',
      height: 220,
      toolbar: {
        show: false
      }
    },
    labels: chartData.map(item => item.estatus),
    colors: chartData.map(item => item.color),
    legend: {
      show: false
    },
    plotOptions: {
      pie: {
        donut: {
          size: '70%',
          labels: {
            show: false
          }
        }
      }
    },
    dataLabels: {
      enabled: false
    },
    tooltip: {
      y: {
        formatter: function (val) {
          const porcentaje = ((val / total) * 100).toFixed(1);
          return `${val} solicitudes (${porcentaje}%)`;
        }
      }
    },
    stroke: {
      width: 2,
      colors: ['#fff']
    },
    responsive: [{
      breakpoint: 480,
      options: {
        chart: {
          height: 200
        }
      }
    }]
  };

  const series = chartData.map(item => item.cantidad);

  const isLoading = loading || !kpis;

  return (
    <Card className="card-height-100" style={{position: 'relative'}}>
      <CardHeader className="align-items-center d-flex">
        {isLoading ? (
           <h4 className="card-title mb-0 flex-grow-1 placeholder-glow">
             <span className="placeholder col-6"></span>
           </h4>
        ) : (
           <>
             <h4 className="card-title mb-0 flex-grow-1">Solicitudes Próximo Mes</h4>
             <div className="flex-shrink-0">
               <span className="badge bg-info text-white fs-12">{total} Por Renovar</span>
             </div>
           </>
        )}
      </CardHeader>
      <CardBody>
        {isLoading ? (
          <>
            <div className="text-center mb-4 placeholder-glow">
               <div className="avatar-xl mx-auto rounded-circle placeholder opacity-25"></div>
            </div>
            <div className="mt-3">
               {[1].map(i => (
                 <div key={i} className="d-flex align-items-center justify-content-between mb-3 placeholder-glow">
                    <div className="d-flex align-items-center">
                       <span className="avatar-xs me-3 placeholder rounded opacity-25"></span>
                       <span className="placeholder" style={{width: '120px'}}></span>
                    </div>
                    <span className="placeholder col-2"></span>
                 </div>
               ))}
            </div>
          </>
        ) : total > 0 ? (
          <>
            {/* Gráfica de Dona */}
            <div className="text-center mb-4">
              <ReactApexChart
                options={chartOptions}
                series={series}
                type="donut"
                height={220}
              />
            </div>

            {/* Lista de Detalles */}
            <div className="mt-3">
              {completeData.map((item) => {
                const porcentaje = total > 0 ? ((item.cantidad / total) * 100).toFixed(1) : 0;
                
                return (
                  <div key={item.estatus} className="d-flex align-items-center justify-content-between mb-3">
                    <div className="d-flex align-items-center">
                      <div 
                        className="avatar-xs me-3"
                        style={{
                          backgroundColor: estatusConfig[item.estatus]?.bgColor || "rgba(135, 138, 153, 0.1)",
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <i 
                          className={`${item.icon} fs-16`}
                          style={{ color: item.color }}
                        ></i>
                      </div>
                      <div>
                        <p className="text-dark mb-0 fw-medium fs-14">{item.estatus}</p>
                      </div>
                    </div>
                    
                    <div className="d-flex align-items-center">
                      <div className="text-end me-3">
                        <span className="text-dark fw-medium fs-14">
                          {item.cantidad}
                        </span>
                      </div>
                      <div className="text-end" style={{ minWidth: '45px' }}>
                        <span 
                          className="fs-12 fw-medium"
                          style={{ 
                            color: item.cantidad > 0 ? item.color : '#878a99'
                          }}
                        >
                          {porcentaje}%
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="text-center py-5">
            <div className="avatar-lg mx-auto mb-3">
              <div className="avatar-title bg-info-subtle text-info rounded-circle fs-24">
                <i className="ri-refresh-line"></i>
              </div>
            </div>
            <h5 className="text-muted">No hay solicitudes por renovar</h5>
            <p className="text-muted mb-0">
              Las solicitudes crónicas aparecerán aquí cuando tengan otro mes de entrega
            </p>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default SolicitudesProximoMes;
